import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Restaurant } from '../types';

// The one wheel (out of a restaurant's whole library) a not-yet-logged-in visitor can be
// told about — just enough to show a "log in to spin" banner, nothing about its prizes or
// settings (see the anon column-grant on lucky_wheels in the Lucky Bite v2 migration).
export interface LiveWheelInfo { id: string; name: string; mode: 'scheduled' | 'manual'; }

interface RestaurantCtx {
  restaurant: Restaurant | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  liveWheel: LiveWheelInfo | null;
}

const RestaurantContext = createContext<RestaurantCtx>({ restaurant: null, loading: true, error: null, refetch: async () => {}, liveWheel: null });
export const useRestaurant = () => useContext(RestaurantContext);

// Columns safe to expose to a fully anonymous visitor (no session at all) — everything the
// public menu / cart / wheel / loyalty / discover UI actually reads. Deliberately excludes
// internal/billing/PII columns (plan_notes, custom_price_eur, paypal_subscription_id,
// pending_admin_email, subscription_status, auto_renew_enabled, plan, plan_expires_at,
// signup_problems, etc.) — those are only readable by an authenticated admin/superadmin
// session, which keeps its own `.select('*')` below since the `authenticated` Postgres role's
// table grant was intentionally left untouched (RLS still scopes writes to `is_admin_of`).
// Mirrors the anon column-level GRANT applied live on `public.restaurants` — keep both in sync.
const PUBLIC_SAFE_COLUMNS = [
  'id', 'slug', 'name', 'tagline', 'description', 'about', 'address', 'phone', 'opening_hours',
  'website', 'instagram', 'facebook', 'logo_url', 'icon_url', 'cover_url', 'accent_color',
  'currency', 'is_active', 'active_from', 'active_until', 'lat', 'lng', 'google_review_url',
  'business_type', 'city', 'menu_source_id', 'delivery_fee', 'delivery_radius_km',
  'min_order_amount', 'delivery_hours', 'delivery_hours_enabled', 'delivery_open_time',
  'delivery_close_time', 'shop_enabled', 'clients_enabled', 'dine_in_enabled',
  'wheel_enabled', 'wheel_campaign_start', 'wheel_campaign_end', 'wheel_max_winners',
  'wheel_share_type', 'wheel_share_url', 'wheel_share_required', 'loyalty_enabled',
  'loyalty_visits_required', 'loyalty_reward_label', 'loyalty_reward_type',
  'loyalty_discount_percent', 'loyalty_reward_menu_item_id', 'loyalty_window_days',
  'loyalty_min_order_amount', 'publicly_listed', 'daily_card_enabled', 'reservations_enabled',
].join(',');

function saveRestaurantCache(slug: string, data: Restaurant) {
  try { localStorage.setItem(`restaurant_${slug}`, JSON.stringify(data)); } catch {}
}

function loadRestaurantCache(slug: string): Restaurant | null {
  try {
    const raw = localStorage.getItem(`restaurant_${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function RestaurantProvider({ children }: { children: React.ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(() => slug ? loadRestaurantCache(slug) : null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const fetchRestaurant = useCallback(async () => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    try {
      // A logged-in session (admin viewing their own restaurant, superadmin, or a customer
      // account) keeps full-column access — the `authenticated` role's table grant is untouched.
      // A fully anonymous visitor (the common case for a public menu view) only has column-level
      // grant on the safe subset now, so request exactly that subset instead of '*' — Postgres
      // errors "permission denied for table" on '*' when the role lacks SELECT on every column,
      // even for a plain SELECT that never touches the restricted ones.
      const { data: { session } } = await supabase.auth.getSession();
      let { data, error: err } = session
        ? await supabase.from('restaurants').select('*').eq('slug', slug).single()
        : await supabase.from('restaurants').select(PUBLIC_SAFE_COLUMNS).eq('slug', slug).single();
      // A column added by a not-yet-applied migration must never take the whole public menu down:
      // retry without the newest optional columns so the menu still loads (those features are
      // just off) — list every column a migration might not have landed for yet.
      if (err && !session) {
        const retry = await supabase.from('restaurants')
          .select(PUBLIC_SAFE_COLUMNS
            .replace(',daily_card_enabled', '').replace(',reservations_enabled', '')
            .replace(',delivery_hours_enabled', '').replace(',delivery_open_time', '').replace(',delivery_close_time', ''))
          .eq('slug', slug).single();
        data = retry.data as typeof data; err = retry.error;
      }
      if (err || !data) throw err || new Error('not found');
      setRestaurant(data as unknown as Restaurant);
      setError(null);
      saveRestaurantCache(slug, data);
    } catch {
      const cached = loadRestaurantCache(slug);
      if (cached) {
        setRestaurant(cached);
        setError(null);
      } else {
        setError('Restoranti nuk u gjet.');
      }
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => { fetchRestaurant(); }, [fetchRestaurant]);

  // Picks the live wheel the same way wheel-ops does server-side (most-recently-updated
  // enabled wheel; live if it has no window, or if now falls inside it) — duplicated
  // client-side because this only needs to answer "show the banner or not", not enforce
  // anything (the edge function is still the real trust boundary).
  const [liveWheel, setLiveWheel] = useState<LiveWheelInfo | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!restaurant?.id) { setLiveWheel(null); return; }
    supabase.from('lucky_wheels').select('id, name, mode, enabled, start_at, end_at')
      .eq('restaurant_id', restaurant.id).eq('enabled', true).order('updated_at', { ascending: false })
      .then(
        ({ data }) => {
          if (cancelled || !data) return;
          const now = new Date();
          const live = (data as any[]).find(w =>
            !w.start_at || !w.end_at || (new Date(w.start_at) <= now && now <= new Date(w.end_at))
          );
          setLiveWheel(live ? { id: live.id, name: live.name, mode: live.mode } : null);
        },
        () => { if (!cancelled) setLiveWheel(null); }
      );
    return () => { cancelled = true; };
  }, [restaurant?.id]);

  return (
    <RestaurantContext.Provider value={{ restaurant, loading, error, refetch: fetchRestaurant, liveWheel }}>
      {children}
    </RestaurantContext.Provider>
  );
}
